import { ref, watch } from 'vue'
import i18n from '@/i18n'

// Shared, module-level store for GET /api/services/massage-types?lang=…
// One request per locale (cached), shared by ServicesSection and the booking sheet.

const massageTypes = ref([])
const isLoading = ref(false)
const error = ref(null)

const cache = new Map() // locale -> mapped array
const inflight = new Map() // locale -> promise

const currentLocale = () => i18n.global.locale.value || 'en'

const mapType = (type) => ({
    id: type.id,
    name: type.name,
    description: type.description,
    priceAdjustment: parseFloat(type.priceAdjustment || 0),
    priceAdjustmentMap: type.priceAdjustmentMap || null,
    popular: type.popular,
    imageUrl: type.imageUrl || null,
    forceDeposit: type.forceDeposit ?? false,
    // Dedicated pair massage: always booked for exactly 2 persons (backend rejects
    // anything else with PAIR_MASSAGE_REQUIRES_TWO_PERSONS).
    pairMassage: type.pairMassage ?? false
})

const fetchForLocale = (lang) => {
    if (cache.has(lang)) return Promise.resolve(cache.get(lang))
    if (inflight.has(lang)) return inflight.get(lang)

    isLoading.value = true
    const promise = (async () => {
        try {
            const response = await fetch(`/api/services/massage-types?lang=${lang}`)
            if (!response.ok) throw new Error(`Massage types API failed: ${response.status}`)
            const data = await response.json()

            // API order is the owner's display order (sortOrder asc): never re-sort
            // here. "Popular" is a badge only, not a position.
            const mapped = (data.data || [])
                .filter((type) => !String(type.name || '').includes('System Break'))
                .map(mapType)

            cache.set(lang, mapped)
            error.value = null
            return mapped
        } catch (err) {
            error.value = err
            console.error('[MassageTypes]', err.message)
            return cache.get(lang) || []
        } finally {
            isLoading.value = false
            inflight.delete(lang)
        }
    })()

    inflight.set(lang, promise)
    return promise
}

const load = async () => {
    const lang = currentLocale()
    const list = await fetchForLocale(lang)
    // Ignore a late response for a locale the user has already navigated away from.
    if (lang === currentLocale()) massageTypes.value = list
    return massageTypes.value
}

// One watcher for the whole app; every consumer sees the new language at once.
watch(i18n.global.locale, () => { load() })

const findById = (id) => (id ? massageTypes.value.find((t) => t.id === id) || null : null)

export const useMassageTypes = () => ({ massageTypes, isLoading, error, load, findById })
